import { baseUrl } from "../connections";
import {
  getMockActiveCampaigns,
  getMockCampaignDetail,
  getMockCampaigns,
  getMockUserTickets,
  reserveMockCampaignTicket,
  retryMockCampaignPayment,
} from "./mockCampaignData";

const parseJsonResponse = async (res) => {
  const text = await res.text();
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch (_error) {
    data = { message: text };
  }

  if (!res.ok) {
    throw new Error(data?.message || data?.error || "Campaign request failed");
  }

  return data;
};

const parseBlobResponse = async (res) => {
  const contentType = res.headers.get("content-type") || "";

  if (!res.ok) {
    let message = "Campaign request failed";
    try {
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      message = data?.message || data?.error || text || message;
    } catch (_error) {
      // Keep the default message when the failed download is not text/json.
    }
    throw new Error(message);
  }

  if (contentType.includes("application/json")) {
    const data = await res.json();
    return {
      data,
      contentType,
      filename: getDownloadFilename(res.headers.get("content-disposition")),
      url:
        data?.pdf_url ||
        data?.ticket_pdf_url ||
        data?.download_url ||
        data?.url ||
        data?.data?.pdf_url ||
        data?.data?.ticket_pdf_url ||
        data?.data?.download_url ||
        data?.data?.url,
    };
  }

  return {
    blob: await res.blob(),
    contentType: contentType || "application/pdf",
    filename: getDownloadFilename(res.headers.get("content-disposition")),
  };
};

const getDownloadFilename = (contentDisposition = "") => {
  const disposition = contentDisposition || "";
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/"/g, ""));

  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] || "campaign-ticket.pdf";
};

const request = ({ path, token, method = "GET", body }) => {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-auth-token"] = token;

  return fetch(`${baseUrl}${path}`, {
    method,
    headers,
    cache: "no-store",
    body: body ? JSON.stringify(body) : undefined,
  }).then(parseJsonResponse);
};

const requestBlob = ({ path, token }) => {
  const headers = {};
  if (token) headers["x-auth-token"] = token;

  return fetch(`${baseUrl}${path}`, {
    method: "GET",
    headers,
    cache: "no-store",
  }).then(parseBlobResponse);
};

const decodeStripeKey = (value) => {
  if (typeof value !== "string") return "";
  if (value.startsWith("pk_")) return value;
  try {
    return atob(value);
  } catch (_error) {
    return value;
  }
};

export const fetchStripeKey = async () => {
  const res = await fetch(`${baseUrl}/config/key`, { cache: "no-store" });
  const text = await res.text();

  if (!res.ok) {
    throw new Error(text || "Unable to load Stripe key");
  }

  try {
    const data = text ? JSON.parse(text) : {};
    const key =
      data?.key ||
      data?.publishableKey ||
      data?.publishable_key ||
      data?.data?.key ||
      data?.data?.publishableKey ||
      data?.data?.publishable_key ||
      data?.data ||
      data;
    return decodeStripeKey(key);
  } catch (_error) {
    return decodeStripeKey(text);
  }
};

const withMockFallback = async (requestFn, fallbackFn) => {
  try {
    return await requestFn();
  } catch (error) {
    if (process.env.REACT_APP_USE_MOCK_CAMPAIGNS === "false") {
      throw error;
    }
    return fallbackFn();
  }
};

const buildQuery = (params) =>
  Object.entries(params)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    )
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

// `city` is a city uuid. Omitting it lets the backend fall back to the city on
// the user's profile, which is what a direct visit or refresh should do.
export const fetchActiveCampaigns = ({ token, limit = 5, city }) =>
  withMockFallback(
    () =>
      request({
        path: `/campaigns/active?${buildQuery({ limit, city })}`,
        token,
      }),
    () => getMockActiveCampaigns(limit),
  );

export const fetchCampaigns = ({ token, city }) => {
  const query = buildQuery({ city });

  return withMockFallback(
    () =>
      request({ path: query ? `/campaigns?${query}` : "/campaigns", token }),
    getMockCampaigns,
  );
};

export const fetchCampaignDetail = ({ campaignUuid, token }) =>
  withMockFallback(
    () => request({ path: `/campaigns/${campaignUuid}`, token }),
    () => getMockCampaignDetail(campaignUuid),
  );

export const reserveCampaignTicket = ({ campaignUuid, token }) =>
  withMockFallback(
    () =>
      request({
        path: `/campaigns/${campaignUuid}/reserve`,
        method: "POST",
        token,
      }),
    () => reserveMockCampaignTicket(campaignUuid),
  );

export const retryCampaignPayment = ({ campaignUuid, token }) =>
  withMockFallback(
    () =>
      request({
        path: `/campaigns/${campaignUuid}/payment-init`,
        method: "POST",
        token,
      }),
    () => retryMockCampaignPayment(campaignUuid),
  );

export const initCampaignPayment = retryCampaignPayment;

export const fetchUserTickets = ({ token }) =>
  withMockFallback(
    () => request({ path: "/campaigns/user/tickets", token }),
    getMockUserTickets,
  );

export const sendCampaignTicketEmail = ({ ticketUuid, token }) =>
  request({
    path: `/campaigns/user/tickets/${ticketUuid}/send-email`,
    method: "POST",
    token,
  });

export const downloadCampaignTicket = ({ ticketUuid, token }) =>
  requestBlob({
    path: `/campaigns/user/tickets/${ticketUuid}/download`,
    token,
  });
